import { clearSession, getCurrentUser, SessionMid } from '@back-office/core';
import { applySnapshot, getSnapshot, types } from 'mobx-state-tree';

const SourceOfFundModel = types.model('SourceOfFund', {
  code: types.optional(types.string, ''),
  isAccepted: types.maybeNull(types.boolean),
});

const RequesterModel = types.model('Requesters', {
  id: types.optional(types.string, ''),
  tid: types.optional(types.string, ''),
  address1: types.optional(types.string, ''),
  address2: types.optional(types.string, ''),
  city: types.optional(types.string, ''),
  province: types.optional(types.string, ''),
  zipcode: types.optional(types.string, ''),
  country: types.optional(types.string, ''),
  sourceOfFunds: types.array(SourceOfFundModel),
  createdAt: types.optional(types.string, ''),
  updatedAt: types.optional(types.string, ''),
  updatedBy: types.optional(types.string, ''),
});

const CurrentUserModel = types.model('CurrentUser', {
  email: types.optional(types.string, ''),
  merchantID: types.optional(types.string, ''),
  mid: types.optional(types.string, ''),
  role: types.optional(types.string, ''),
  createdAt: types.optional(types.string, ''),
  invitedAt: types.optional(types.string, ''),
  status: types.optional(types.string, ''),
  merchantName: types.optional(types.string, ''),
  requesters: types.array(RequesterModel),
  permissionId: types.optional(types.number, 0),
  permissions: types.maybeNull(types.array(types.string)),
  visibleBillerRequest: types.optional(types.boolean, false),
  visiblePaymentRequest: types.optional(types.boolean, false),
  isRecurringMerchant: types.optional(types.boolean, false),
  isEnrollmentType: types.optional(types.boolean, false),
});

interface EnhancedSessionMid extends SessionMid {
  visibleBillerRequest: boolean;
  visiblePaymentRequest: boolean;
  isRecurringMerchant: boolean;
  isEnrollmentType: boolean;
}

const mapUserProperties = (user: SessionMid): EnhancedSessionMid => ({
  visibleBillerRequest: user.visible_biller_request,
  visiblePaymentRequest: user.visible_payment_request,
  isRecurringMerchant: user.is_recurring_merchant,
  isEnrollmentType: user.enrollment_type,
  ...user,
});

const AuthModel = types
  .model('Auth', {
    isAuth: types.optional(types.boolean, false),
    token: types.optional(types.string, ''),
    currentUser: CurrentUserModel,
  })
  .actions((self) => {
    let initialState = {};
    return {
      afterCreate() {
        initialState = getSnapshot(self);
      },
      afterAttach() {
        if (
          !['/forget_password', '/invitations'].includes(
            window.location.pathname
          )
        ) {
          const currentUser = getCurrentUser();
          if (currentUser) {
            applySnapshot(self, {
              ...self,
              isAuth: true,
              currentUser: mapUserProperties(currentUser),
            });
          } else {
            clearSession();
            applySnapshot(self, initialState);
          }
        } else {
          clearSession();
          applySnapshot(self, initialState);
        }
      },
      reset() {
        applySnapshot(self, initialState);
      },
      setAuth(isAuth: boolean) {
        applySnapshot(self, { ...self, isAuth });
      },
      setCurrentUser(currentUser: SessionMid) {
        applySnapshot(self, {
          ...self,
          currentUser: mapUserProperties(currentUser),
        });
      },
      setToken(token: string) {
        applySnapshot(self, { ...self, token });
      },
    };
  });

export default AuthModel;
